"use client";

import ShapeFactory from "@tosize/shapes/src/lib/factory/ShapeFactory";
import { getFetcher, useShapeInputs } from "@tosize/shapes/src/lib/swr";
import type { Workpiece } from "@tosize/shapes/src/types/workpiece";
import { type FC, type PropsWithChildren, useMemo } from "react";
import { useIntl } from "react-intl";
import { DEFAULT_THICKNESS, type ExtendedVisuProps, MODIFIER } from ".";
import { PanelVisualisationContext } from "./Context";
import { getPanelSize } from "./utils/Sizes";

type Props = Pick<
	ExtendedVisuProps,
	"lang" | "apiToken" | "cartId" | "apiUrl" | "productId" | "shape" | "workpiece" | "thickness"
>;

export const PanelVisualisationProvider: FC<PropsWithChildren<Props>> = ({
	lang,
	children,
	...props
}) => {
	const intl = useIntl();
	const fetcher = getFetcher(props.apiToken, props.cartId);
	const response = useShapeInputs(props.apiUrl, props.productId, null, { fetcher });

	const shape = useMemo(() => {
		const shapes = response.data ? Object.values(response.data) : undefined;

		return props.shape ?? shapes?.find((shape) => shape.code === props.workpiece.shape);
	}, [props.shape, props.workpiece.shape, response.data]);

	const shapeModel = useMemo(() => {
		return shape ? ShapeFactory.getInstance(shape) : undefined;
	}, [shape]);

	const dimensions = useMemo(() => {
		return (
			shapeModel?.getDimensions(props.workpiece.shape_dimensions) ??
			props.workpiece.shape_dimensions
		);
	}, [props.workpiece.shape_dimensions, shapeModel]);

	const workpiece = useMemo(() => {
		return { ...props.workpiece, shape_dimensions: dimensions } as Workpiece;
	}, [dimensions, props.workpiece]);

	const isModified = useMemo(() => {
		return Object.values(props.workpiece.shape_dimensions).some((value) => !!value);
	}, [props.workpiece.shape_dimensions]);

	const sidePaths = useMemo(() => {
		return dimensions ? shapeModel?.getThreeSidePaths(dimensions) : undefined;
	}, [dimensions, shapeModel]);

	const inputLines = useMemo(() => {
		return dimensions ? shapeModel?.getThreeInputLines(dimensions) : undefined;
	}, [dimensions, shapeModel]);

	const dimensionsString = useMemo(() => {
		return dimensions ? shapeModel?.getDimensionsString(dimensions, intl) : undefined;
	}, [dimensions, intl, shapeModel]);

	const { length, width } = getPanelSize(workpiece);

	const thickness = useMemo(() => {
		return (props.thickness ?? DEFAULT_THICKNESS) / MODIFIER;
	}, [props.thickness]);

	return (
		<PanelVisualisationContext.Provider
			value={{
				lang,
				shape,
				shapeModel,
				dimensions,
				workpiece,
				isModified,
				sidePaths,
				inputLines,
				dimensionsString,
				length,
				width,
				thickness,
			}}
		>
			{children}
		</PanelVisualisationContext.Provider>
	);
};
